/**
 * AI Insight Utilities
 *
 * Generates simple, conversational insights for non-technical users
 * from aggregated problem breakdown data.
 */

/**
 * Generate the main insight text for the chip display
 * Uses prioritized rules to show the most relevant insight
 * Text should be narrative and read like a sentence with full context
 *
 * @param {Object} breakdown - Breakdown data from API
 * @param {number} totalCost - Total opportunity cost for the group
 * @param {number} machineCount - Number of machines in the group
 * @param {Function} t - Translation function
 * @returns {Object} { type: string, text: string }
 */
export const generateInsightText = (breakdown, totalCost, machineCount, t) => {
  if (!breakdown) {
    // Default: per-machine loss when no breakdown available
    const perMachine = machineCount > 0 ? Math.round(totalCost / machineCount) : 0;
    return {
      type: 'normalized',
      text: t('chipLossPerMachine', { amount: perMachine, defaultValue: `Avg €${perMachine} loss per machine` })
    };
  }

  const {
    avgPerformanceRatio,
    topProblems = [],
    categoryBreakdown = {},
    problemsOverThirtyDays = 0
  } = breakdown;

  // Rule 1: Very poor performance (<15% of benchmark) - show performance chip
  if (avgPerformanceRatio !== null && avgPerformanceRatio < 0.15) {
    const pct = Math.round(avgPerformanceRatio * 100);
    return {
      type: 'performance',
      text: t('chipSellingWellBelow', { percentage: pct, defaultValue: `Products selling at ${pct}% of normal` })
    };
  }

  // Rule 3: High concentration (top 3 = >60% of loss)
  if (topProblems.length >= 3 && totalCost > 0) {
    const uniqueProducts = deduplicateTopProblems(topProblems);
    const top3Cost = uniqueProducts.slice(0, 3).reduce((sum, p) => sum + (p.cost || 0), 0);
    const pct = Math.round((top3Cost / totalCost) * 100);
    if (pct > 60) {
      return {
        type: 'concentration',
        text: t('chipConcentratedLoss', { percentage: pct, defaultValue: `${pct}% of loss in just 3 products` })
      };
    }
  }

  // Rule 4: Category pattern (>50% one category)
  const categoryEntries = Object.entries(categoryBreakdown);
  if (categoryEntries.length > 0) {
    const totalProblems = categoryEntries.reduce((sum, [, count]) => sum + count, 0);
    const topCategory = categoryEntries.sort((a, b) => b[1] - a[1])[0];
    if (topCategory && totalProblems > 0 && topCategory[1] / totalProblems > 0.5) {
      const pct = Math.round((topCategory[1] / totalProblems) * 100);
      return {
        type: 'category',
        text: t('chipMostlyCategory', { percentage: pct, category: topCategory[0], defaultValue: `${pct}% of problems are ${topCategory[0]}` })
      };
    }
  }

  // Rule 5: Old problems (>40% over 30 days)
  if (machineCount > 0 && problemsOverThirtyDays > machineCount * 0.4) {
    return {
      type: 'urgency',
      text: t('chipOldProblems', { count: problemsOverThirtyDays, defaultValue: `${problemsOverThirtyDays} problems older than 30 days` })
    };
  }

  // Default: Simple per-machine loss
  const perMachine = machineCount > 0 ? Math.round(totalCost / machineCount) : 0;
  return {
    type: 'normalized',
    text: t('chipLossPerMachine', { amount: perMachine, defaultValue: `Avg €${perMachine} loss per machine` })
  };
};

/**
 * Deduplicate top problems by product name, summing costs
 * @param {Array} topProblems - Array of top problems (may have duplicates)
 * @returns {Array} Deduplicated array sorted by cost desc
 */
export const deduplicateTopProblems = (topProblems = []) => {
  const byProduct = {};

  for (const problem of topProblems) {
    const name = problem.productName || 'Unknown';
    if (!byProduct[name]) {
      byProduct[name] = {
        productName: name,
        cost: 0,
        performanceRatio: null,
        count: 0
      };
    }
    byProduct[name].cost += (problem.cost || 0);
    byProduct[name].count += 1;
    // Keep the lowest performance ratio (worst performer)
    if (problem.performanceRatio !== null) {
      if (byProduct[name].performanceRatio === null || problem.performanceRatio < byProduct[name].performanceRatio) {
        byProduct[name].performanceRatio = problem.performanceRatio;
      }
    }
  }

  return Object.values(byProduct).sort((a, b) => b.cost - a.cost);
};

/**
 * Get performance health label and color
 * @param {number|null} ratio - avgPerformanceRatio (0-1 scale)
 * @param {Function} t - Translation function
 * @returns {Object} { label: string, color: string, percentage: number }
 */
export const getPerformanceHealth = (ratio, t) => {
  if (ratio === null || ratio === undefined) {
    return {
      label: t('noData', { defaultValue: 'No data' }),
      color: '#9CA3AF',
      percentage: 0
    };
  }

  const percentage = Math.round(ratio * 100);

  if (ratio < 0.4) {
    return {
      label: t('severelyUnderperforming', { defaultValue: 'severely underperforming' }),
      color: '#DC2626',
      percentage
    };
  }
  if (ratio < 0.6) {
    return {
      label: t('underperforming', { defaultValue: 'underperforming' }),
      color: '#F59E0B',
      percentage
    };
  }
  if (ratio < 0.8) {
    return {
      label: t('slightlyBelowAverage', { defaultValue: 'slightly below average' }),
      color: '#EAB308',
      percentage
    };
  }
  return {
    label: t('performingWell', { defaultValue: 'performing well' }),
    color: '#22C55E',
    percentage
  };
};

/**
 * Calculate loss concentration metrics
 * @param {Array} topProblems - Array of top problems
 * @param {number} totalCost - Total opportunity cost
 * @returns {Object} { top3Percentage: number, formatted: string }
 */
export const getLossConcentration = (topProblems = [], totalCost) => {
  if (!topProblems.length || !totalCost) {
    return { top3Percentage: 0, formatted: '0%' };
  }

  // Deduplicate first
  const uniqueProducts = deduplicateTopProblems(topProblems);
  const top3Cost = uniqueProducts.slice(0, 3).reduce((sum, p) => sum + (p.cost || 0), 0);
  const percentage = Math.round((top3Cost / totalCost) * 100);

  return {
    top3Percentage: percentage,
    formatted: `${percentage}%`
  };
};

/**
 * Format currency for display
 * @param {number} value - Currency value
 * @returns {string} Formatted string
 */
export const formatCurrency = (value) => {
  if (!value || isNaN(value)) return '€0';
  return `€${Math.abs(parseFloat(value)).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  })}`;
};

/**
 * Get category breakdown as sorted array with percentages
 * @param {Object} categoryBreakdown - { categoryName: count }
 * @returns {Array} [{ name, count, percentage }]
 */
export const getCategoryBreakdownArray = (categoryBreakdown = {}) => {
  const entries = Object.entries(categoryBreakdown);
  if (!entries.length) return [];

  const total = entries.reduce((sum, [, count]) => sum + count, 0);

  return entries
    .map(([name, count]) => ({
      name,
      count,
      percentage: total > 0 ? Math.round((count / total) * 100) : 0
    }))
    .sort((a, b) => b.count - a.count);
};
